// src/dashboard/MyProfile.jsx

import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { AuthContext } from '../context/AuthContext';
import { db } from '../config/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { User as UserIcon } from 'lucide-react';

export default function MyProfile() {
  const intl = useIntl();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Campos editáveis básicos
  const [form, setForm] = useState({
    name: '',
    phone: '',
    creci: '',
  });

  // Metadados somente leitura
  const [meta, setMeta] = useState({
    email: '',
    role: '',
    active: true,
    photoURL: '',
  });

  // Carrega o doc do próprio usuário (users/{uid})
  useEffect(() => {
    const load = async () => {
      if (!user?.uid) return;
      setLoading(true);
      try {
        const ref = doc(db, 'users', user.uid);
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};

        setForm({
          name: data.name || user.displayName || '',
          phone: data.phone || '',
          creci: data.creci || '',
        });

        setMeta({
          email: data.email || user.email || '',
          role: data.role || 'owner',
          active: typeof data.active === 'boolean' ? data.active : true,
          photoURL: data.photoURL || user.photoURL || '',
        });
      } catch (err) {
        console.error('profile.load.error', err);
        toast.error(
          intl.formatMessage({
            id: 'profile.loadError',
            defaultMessage: 'Não foi possível carregar seu perfil.',
          })
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid, intl]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;
    setSaving(true);
    try {
      const ref = doc(db, 'users', user.uid);
      await setDoc(
        ref,
        {
          // editáveis
          name: form.name || '',
          phone: form.phone || '',
          creci: form.creci || '',
          // assegura persistência de email/foto para consistência
          email: meta.email || user.email || '',
          photoURL: meta.photoURL || user.photoURL || '',
          // mantém flags importantes
          active: typeof meta.active === 'boolean' ? meta.active : true,
          role: meta.role || 'owner',
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      toast.success(
        intl.formatMessage({
          id: 'profile.saved',
          defaultMessage: 'Perfil atualizado com sucesso.',
        })
      );
    } catch (err) {
      console.error('profile.save.error', err);
      toast.error(
        intl.formatMessage({
          id: 'profile.saveError',
          defaultMessage: 'Erro ao salvar seu perfil.',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="la dashboard-page">
      <div className="dashboard-content">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <h2 className="page-title">
              {intl.formatMessage({ id: 'menu.my-profile', defaultMessage: 'Meu Perfil' })}
            </h2>
            <div className="page-subtitle">
              <UserIcon size={16} style={{ marginRight: 6 }} />
              <span>{meta.email}</span>
            </div>
          </div>
        </div>

        {/* Card do Perfil */}
        <div className="card" role="region" aria-labelledby="profile-section">
          <div className="card-header">
            <h3 id="profile-section" className="card-title">
              {intl.formatMessage({ id: 'profile.title', defaultMessage: 'Dados do perfil' })}
            </h3>
            <p className="card-subtitle">
              {intl.formatMessage({
                id: 'profile.subtitle',
                defaultMessage:
                  'Atualize seu nome, telefone e CRECI. Seu e-mail é usado para login e não pode ser alterado aqui.',
              })}
            </p>
          </div>

          {loading ? (
            <p className="muted-text">
              {intl.formatMessage({ id: 'loading.label', defaultMessage: 'Carregando...' })}
            </p>
          ) : (
            <form className="form-grid" onSubmit={onSave}>
              {/* Avatar + Email (read-only) */}
              <div className="form-row">
                <label className="form-label">
                  {intl.formatMessage({ id: 'profile.email', defaultMessage: 'E-mail' })}
                </label>
                <input
                  type="email"
                  className="input-field"
                  value={meta.email}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-row">
                <label className="form-label">
                  {intl.formatMessage({ id: 'profile.name', defaultMessage: 'Nome' })}
                </label>
                <input
                  name="name"
                  type="text"
                  className="input-field"
                  value={form.name}
                  onChange={onChange}
                  placeholder={intl.formatMessage({
                    id: 'profile.name.placeholder',
                    defaultMessage: 'Seu nome completo',
                  })}
                />
              </div>

              <div className="form-row">
                <label className="form-label">
                  {intl.formatMessage({ id: 'profile.phone', defaultMessage: 'Telefone' })}
                </label>
                <input
                  name="phone"
                  type="tel"
                  className="input-field"
                  value={form.phone}
                  onChange={onChange}
                  placeholder={intl.formatMessage({
                    id: 'profile.phone.placeholder',
                    defaultMessage: '(11) 90000-0000',
                  })}
                />
              </div>

              <div className="form-row">
                <label className="form-label">
                  {intl.formatMessage({ id: 'profile.creci', defaultMessage: 'CRECI' })}
                </label>
                <input
                  name="creci"
                  type="text"
                  className="input-field"
                  value={form.creci}
                  onChange={onChange}
                  placeholder={intl.formatMessage({
                    id: 'profile.creci.placeholder',
                    defaultMessage: 'Ex.: SP-12345-F',
                  })}
                />
              </div>

              {/* Somente leitura: função e status */}
              <div className="form-row">
                <label className="form-label">
                  {intl.formatMessage({ id: 'profile.role', defaultMessage: 'Função' })}
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={
                    meta.role === 'owner'
                      ? intl.formatMessage({ id: 'user.role.owner', defaultMessage: 'Proprietário' })
                      : meta.role === 'editor'
                      ? intl.formatMessage({ id: 'user.role.editor', defaultMessage: 'Editor' })
                      : meta.role === 'corretor'
                      ? intl.formatMessage({ id: 'user.role.corretor', defaultMessage: 'Corretor' })
                      : meta.role
                  }
                  disabled
                  readOnly
                />
              </div>

              <div className="form-row">
                <label className="form-label">
                  {intl.formatMessage({ id: 'profile.status', defaultMessage: 'Status' })}
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={
                    meta.active
                      ? intl.formatMessage({ id: 'user.active', defaultMessage: 'Ativo' })
                      : intl.formatMessage({ id: 'user.inactive', defaultMessage: 'Inativo' })
                  }
                  disabled
                  readOnly
                />
              </div>

              <div className="form-actions">
                <button className="primary-button" type="submit" disabled={saving}>
                  {saving
                    ? intl.formatMessage({ id: 'common.saving', defaultMessage: 'Salvando…' })
                    : intl.formatMessage({ id: 'common.save', defaultMessage: 'Salvar alterações' })}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
